import React from "react";
import { Box, Chip, TextField, InputAdornment, FormControlLabel, Switch } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import CustomerSearchAutocomplete from "components/CustomerSearchAutocomplete";
import { BORDER, BG_WHITE, RADIUS_INPUT, TEXT_SUBTLE } from "components/bento/bentoTokens";
import { ViewToggle } from "./parts";

// Date presets: value = days back (null = all time, 0 = today)
const PRESETS = [
  { key: "all", days: null },
  { key: "today", days: 0 },
  { key: "last7", days: 7 },
  { key: "last30", days: 30 },
];

export const presetRange = (days) => {
  if (days === null || days === undefined) return { from: "", to: "" };
  const d = new Date();
  d.setDate(d.getDate() - days);
  const from = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { from, to: "" };
};

const inputSx = {
  "& .MuiOutlinedInput-root": { borderRadius: RADIUS_INPUT, backgroundColor: BG_WHITE },
};

/**
 * Shared filter toolbar for both gallery tabs.
 * filters: { q, customer, preset, from, to, category }
 * categories: [{ value, label }] — contextual chips (photo type / method / status)
 * showGroupToggle + grouped/onGroupedChange: Packages tab only
 * viewMode + onViewModeChange: Tile/List switcher — omit to hide (e.g. Packages
 * tab hides it while "Group by package" is on, since cards aren't a per-photo view)
 */
const FilterBar = ({
  filters,
  onChange,
  categories = [],
  showGroupToggle,
  grouped,
  onGroupedChange,
  viewMode,
  onViewModeChange,
  searchPlaceholder,
  hideCustomerFilter,
}) => {
  const { t } = useTranslation();
  const set = (patch) => onChange({ ...filters, ...patch });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        p: "12px 14px",
        mb: 2,
        border: `1px solid ${BORDER}`,
        borderRadius: "16px",
        backgroundColor: "rgba(250,249,252,.8)",
      }}
    >
      <TextField
        size="small"
        value={filters.q}
        onChange={(e) => set({ q: e.target.value })}
        placeholder={searchPlaceholder || t("gallery.searchPlaceholder")}
        sx={{ width: { xs: "100%", sm: 230 }, ...inputSx }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 17, color: TEXT_SUBTLE }} />
            </InputAdornment>
          ),
        }}
      />
      {!hideCustomerFilter && (
        <CustomerSearchAutocomplete
          value={filters.customer}
          onChange={(customer) => set({ customer })}
          label={t("gallery.customerFilter")}
          sx={{ width: { xs: "100%", sm: 220 }, ...inputSx }}
        />
      )}

      {PRESETS.map(({ key, days }) => (
        <Chip
          key={key}
          label={t(`gallery.preset.${key}`)}
          size="small"
          color="secondary"
          variant={filters.preset === key ? "filled" : "outlined"}
          onClick={() => set({ preset: key, ...presetRange(days) })}
        />
      ))}
      <TextField
        size="small"
        type="date"
        label={t("gallery.fromDate")}
        InputLabelProps={{ shrink: true }}
        value={filters.preset === "custom" ? filters.from : ""}
        onChange={(e) => set({ preset: "custom", from: e.target.value })}
        sx={{ width: 150, ...inputSx }}
      />
      <TextField
        size="small"
        type="date"
        label={t("gallery.toDate")}
        InputLabelProps={{ shrink: true }}
        value={filters.preset === "custom" ? filters.to : ""}
        onChange={(e) => set({ preset: "custom", to: e.target.value })}
        sx={{ width: 150, ...inputSx }}
      />

      {categories.length > 0 && (
        <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>
          {categories.map((c) => (
            <Chip
              key={c.value}
              label={c.label}
              size="small"
              color="secondary"
              variant={filters.category === c.value ? "filled" : "outlined"}
              onClick={() => set({ category: filters.category === c.value ? "" : c.value })}
            />
          ))}
        </Box>
      )}

      {(showGroupToggle || onViewModeChange) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: "auto" }}>
          {showGroupToggle && (
            <FormControlLabel
              sx={{ mr: 0, "& .MuiTypography-root": { fontSize: 12.5 } }}
              control={
                <Switch size="small" color="secondary" checked={grouped} onChange={(e) => onGroupedChange(e.target.checked)} />
              }
              label={t("gallery.groupByPackage")}
            />
          )}
          {onViewModeChange && (
            <ViewToggle value={viewMode} onChange={onViewModeChange} tileLabel={t("gallery.viewTile")} listLabel={t("gallery.viewList")} />
          )}
        </Box>
      )}
    </Box>
  );
};

export default FilterBar;
